/**
 * Allowlist HTML sanitizer for externally authored article content.
 *
 * Runs in the Cloudflare Worker runtime (no DOM, no jsdom), so it is a
 * tokenizer over the raw markup rather than a DOM-based cleaner. Anything not
 * explicitly allowed is dropped.
 *
 * Used on write (server function, authoritative) and again on render.
 */

const VOID_TAGS = new Set(["br", "hr", "img"]);

/** Tags whose entire contents are removed, not just the tag. */
const STRIPPED_WITH_CONTENT = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "svg",
  "math",
  "template",
  "noscript",
  "form",
  "input",
  "button",
  "select",
  "textarea",
  "link",
  "meta",
  "base",
];

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "hr",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "small",
  "sub",
  "sup",
  "ul",
  "ol",
  "li",
  "h2",
  "h3",
  "h4",
  "h5",
  "blockquote",
  "a",
  "img",
  "figure",
  "figcaption",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "code",
  "pre",
  "span",
  "div",
  "section",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "target", "rel"]),
  img: new Set(["src", "alt", "title", "width", "height", "loading"]),
  th: new Set(["colspan", "rowspan", "scope"]),
  td: new Set(["colspan", "rowspan"]),
  h2: new Set(["id"]),
  h3: new Set(["id"]),
  h4: new Set(["id"]),
};

const decodeEntities = (value: string) =>
  value
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);?/g, (_, dec: string) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&colon;/gi, ":")
    .replace(/&Tab;|&NewLine;/gi, "");

/**
 * Returns a safe URL string, or null when the URL must be dropped.
 * Allows https/http/mailto/tel absolute URLs, root-relative and anchor links.
 */
export const safeUrl = (raw: string, opts: { allowDataImage?: boolean } = {}): string | null => {
  const value = decodeEntities(raw).trim().replace(/[\u0000-\u001f\u007f]/g, "");
  if (!value) return null;
  if (/^(\/|#)(?!\/)/.test(value)) return value; // /path or #anchor (not protocol-relative)
  const scheme = value.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  if (!scheme) return value.startsWith("//") ? null : value; // plain relative path
  if (scheme === "http" || scheme === "https" || scheme === "mailto" || scheme === "tel") {
    return value;
  }
  if (opts.allowDataImage && /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i.test(value)) {
    return value;
  }
  return null;
};

const escapeAttr = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const parseAttrs = (raw: string): Array<[string, string]> => {
  const out: Array<[string, string]> = [];
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    out.push([m[1]!.toLowerCase(), m[2] ?? m[3] ?? m[4] ?? ""]);
  }
  return out;
};

/** Sanitizes an untrusted HTML fragment down to an allowlist of tags/attributes. */
export const sanitizeArticleHtml = (input: string): string => {
  if (!input) return "";
  let html = String(input);

  // Drop comments (can hide conditional-comment scripting).
  html = html.replace(/<!--[\s\S]*?-->/g, "");
  // Drop dangerous elements together with their contents.
  for (const tag of STRIPPED_WITH_CONTENT) {
    html = html.replace(new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, "gi"), "");
    html = html.replace(new RegExp(`<\\/?${tag}\\b[^>]*>`, "gi"), "");
  }

  const openStack: string[] = [];
  let out = "";
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = tagRe.exec(html))) {
    out += html.slice(lastIndex, m.index);
    lastIndex = tagRe.lastIndex;

    const full = m[0];
    const tag = m[1]!.toLowerCase();
    const isClosing = full.startsWith("</");

    if (!ALLOWED_TAGS.has(tag)) continue;

    if (isClosing) {
      const idx = openStack.lastIndexOf(tag);
      if (idx === -1) continue;
      openStack.splice(idx, 1);
      out += `</${tag}>`;
      continue;
    }

    const allowed = ALLOWED_ATTRS[tag];
    let attrsOut = "";
    if (allowed) {
      for (const [name, value] of parseAttrs(m[2] ?? "")) {
        if (!allowed.has(name)) continue;
        if (name === "href" || name === "src") {
          const url = safeUrl(value, { allowDataImage: tag === "img" });
          if (!url) continue;
          attrsOut += ` ${name}="${escapeAttr(url)}"`;
          continue;
        }
        if (name === "target") {
          if (value !== "_blank") continue;
          attrsOut += ` target="_blank"`;
          continue;
        }
        if (name === "rel") continue; // recomputed below
        if (/^on/i.test(name)) continue;
        attrsOut += ` ${name}="${escapeAttr(value)}"`;
      }
    }

    if (tag === "a") {
      if (!/\shref="/.test(attrsOut)) continue; // link with unsafe/missing href → drop the tag
      attrsOut += ` rel="noopener nofollow ugc"`;
    }
    if (tag === "img") {
      if (!/\ssrc="/.test(attrsOut)) continue;
      if (!/\salt="/.test(attrsOut)) attrsOut += ` alt=""`;
      if (!/\sloading="/.test(attrsOut)) attrsOut += ` loading="lazy"`;
    }

    if (VOID_TAGS.has(tag)) {
      out += `<${tag}${attrsOut} />`;
      continue;
    }
    openStack.push(tag);
    out += `<${tag}${attrsOut}>`;
  }
  out += html.slice(lastIndex);

  // Close anything the author left open.
  for (let i = openStack.length - 1; i >= 0; i--) out += `</${openStack[i]}>`;

  return out.trim();
};

/** Strips all markup — used for excerpts/meta descriptions derived from body. */
export const htmlToText = (input: string): string =>
  sanitizeArticleHtml(input)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 96)
    .replace(/^-|-$/g, "");

export const isValidSlug = (value: string): boolean =>
  /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value) && value.length >= 3 && value.length <= 96;
